import React from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { CATEGORY_ACCESSORY, CATEGORY_ATTIRE, CATEGORY_BRIDAL_GOWNS, CATEGORY_DRESSES, CATEGORY_GOWNS, CATEGORY_OCCASIONS, CATEGORY_SHOES, defaultGownCategory, EXTENDED_DATA_SCHEMA_TYPES, OTHER_BRAND, OVERVIEW, propTypes, styleSubAccessories } from '../../../../util/types';
import { maxLength, required, composeValidators, minLength, yearFormatValid } from '../../../../util/validators';

// Import shared components
import { Form, Button, FieldSelect, FieldTextInput, Heading } from '../../../../components';
// Import modules from this directory
import CustomExtendedDataField from '../CustomExtendedDataField';
import css from './EditListingDetailsForm.module.css';
import { originalOwners, originalReceipts } from '../../../../config/configSearch';
import FieldMultiSelect from '../../../../components/FieldMultiSelect/FieldMultiSelect';
import { DOTS_PROCESS_NAME } from '../../../../transactions/transaction';

const TITLE_MAX_LENGTH = 40;
const TITLE_MIN_LENGTH = 2;

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingDetailsForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingDetailsForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingDetailsForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

// Field component that either allows selecting listing type (if multiple types are available)
// or just renders hidden fields:
// - listingType              Set of predefined configurations for each listing type
// - transactionProcessAlias  Initiate correct transaction against Marketplace API
// - unitType                 Main use case: pricing unit
const FieldSelectListingType = props => {
  const { name, listingTypes, hasExistingListingType, onListingTypeChange, formApi, intl } = props;
  const hasMultipleListingTypes = listingTypes?.length > 1;

  const handleOnChange = value => {
    const selectedListingType = listingTypes.find(config => config.listingType === value);
    formApi.change('transactionProcessAlias', selectedListingType.transactionProcessAlias);
    formApi.change('unitType', selectedListingType.unitType);

    if (onListingTypeChange) {
      onListingTypeChange(selectedListingType);
    }
  };
  const getListingTypeLabel = listingType => {
    const listingTypeConfig = listingTypes.find(config => config.listingType === listingType);
    return listingTypeConfig ? listingTypeConfig.label : listingType;
  };

  return hasMultipleListingTypes && !hasExistingListingType ? (
    <>
      <FieldSelect
        id={name}
        name={name}
        className={css.listingTypeSelect}
        label={intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
        validate={required(
          intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeRequired' })
        )}
        onChange={handleOnChange}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypePlaceholder' })}
        </option>
        {listingTypes.map(config => {
          const type = config.listingType;
          return (
            <option key={type} value={type}>
              {config.label}
            </option>
          );
        })}
      </FieldSelect>
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  ) : hasMultipleListingTypes && hasExistingListingType ? (
    <div className={css.listingTypeSelect}>
      <Heading as="h5" rootClassName={css.selectedLabel}>
        {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
      </Heading>
      <p className={css.selectedValue}>{getListingTypeLabel(formApi.getFieldState(name)?.value)}</p>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </div>
  ) : (
    <>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  );
};

// Add collect data for listing fields (both publicData and privateData) based on configuration
const AddListingFields = props => {
  const { listingType, listingFieldsConfig, intl } = props;
  const fields = listingFieldsConfig.reduce((pickedFields, fieldConfig) => {
    const { key, includeForListingTypes, schemaType, scope } = fieldConfig || {};
    const namespacedKey = scope === 'public' ? `pub_${key}` : `priv_${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetListingType =
      includeForListingTypes == null || includeForListingTypes.includes(listingType);
    const isProviderScope = ['public', 'private'].includes(scope);

    return isKnownSchemaType && isTargetListingType && isProviderScope
      ? [
        ...pickedFields,
        <CustomExtendedDataField
          key={namespacedKey}
          name={namespacedKey}
          fieldConfig={fieldConfig}
          defaultRequiredMessage={intl.formatMessage({
            id: 'EditListingDetailsForm.defaultRequiredMessage',
          })}
        />,
      ]
      : pickedFields;
  }, []);

  return <>{fields}</>;
};

// Form that asks title, description, transaction process and unit type for pricing
// In addition, it asks about custom fields according to marketplace-custom-config.js
const EditListingDetailsFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    keepDirtyOnReinitialize={true}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        formId,
        form: formApi,
        handleSubmit,
        onListingTypeChange,
        intl,
        invalid,
        pristine,
        selectableListingTypes,
        hasExistingListingType,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        productType,
        form,
        publicData,
        onSaveDraft,
        listingTitle,
        history,
        setSaveAsDraft,
        saveAsDraft,
        page
      } = formRenderProps;
      const { listingCategory = [], listingStyle = [], listingDesigner = [], listingSubAccessories = [], listingBrands = [] } = page?.allFieldsData || {};

      const { listingType, brand, category, style, title, projectType, designer, originalOwner, originalReceipt, purchasedYear, role, events } = values;

      const titleRequiredMessage = intl.formatMessage({
        id: 'EditListingDetailsForm.titleRequired',
      });
      const maxLengthMessage = intl.formatMessage(
        { id: 'EditListingDetailsForm.maxLength' },
        {
          maxLength: TITLE_MAX_LENGTH,
        }
      );
      const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);

      const minLengthMessage = intl.formatMessage(
        { id: 'EditListingDetailsForm.minLength' },
        {
          minLength: TITLE_MIN_LENGTH,
        }
      );
      const minLength2Message = minLength(minLengthMessage, TITLE_MIN_LENGTH);

      // brand
      const brandRequired = required(
        intl.formatMessage({
          id: 'EditListingDetailsForm.brandRequired',
        })
      );

      // item
      const subCategoryRequired = required(
        intl.formatMessage({
          id: 'EditListingDetailsForm.subCategoryRequired',
        })
      );

      const yearValid = yearFormatValid(
        intl.formatMessage({
          id: 'EditListingAppearanceForm.yearValid',
        })
      );

      // Show title and description only after listing type is selected
      const showTitle = listingType;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress && !saveAsDraft;
      const submitDisabled =
        invalid ||
        disabled ||
        submitInProgress ||
        !originalOwner?.label ||
        !originalReceipt?.label;

      const filterCategory = listingCategory.filter(sub => (sub.parentKey.includes(productType)));
      const filterStyles = listingStyle.filter(event => (event.parentKey.includes(productType)));
      const filterDesigners = listingDesigner.filter(event => (event.parentKey == productType));
      const filterAccessoriesStyle = listingSubAccessories.filter(e => e.parentKey == productType && e.parentCategory == category?.label);
      const filterSubAccessoriesStyles = (styleSubAccessories.includes(category?.label) || [CATEGORY_GOWNS, CATEGORY_OCCASIONS, CATEGORY_SHOES].includes(publicData?.productType));

      const titlePlaceholder = [CATEGORY_GOWNS].includes(productType) ?
        intl.formatMessage({ id: 'EditListingDetailsForm.titlePlaceholder' })
        : [CATEGORY_OCCASIONS, CATEGORY_ATTIRE].includes(productType) ?
          intl.formatMessage({ id: 'EditListingDetailsForm.occasionTitlePlaceholder' })
          : [CATEGORY_ACCESSORY].includes(productType) ?
            intl.formatMessage({ id: 'EditListingDetailsForm.accessoryTitlePlaceholder' })
            : [CATEGORY_DRESSES].includes(productType) ?
              intl.formatMessage({ id: 'EditListingDetailsForm.dressTitlePlaceholder' })
              : [CATEGORY_SHOES].includes(productType) ?
                intl.formatMessage({ id: 'EditListingDetailsForm.shoesTitlePlaceholder' })
                : null
        ;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <ErrorMessage fetchErrors={fetchErrors} />

          <FieldSelectListingType
            name="listingType"
            listingTypes={selectableListingTypes}
            hasExistingListingType={hasExistingListingType}
            onListingTypeChange={onListingTypeChange}
            formApi={formApi}
            intl={intl}
          />
          {[CATEGORY_OCCASIONS].includes(productType) ? <p className={css.tip}><FormattedMessage id={"EditListingAppearanceForm.disclaimer"} /></p> : null}
          <div className={css.fieldRow}>
            {showTitle ? (
              <FieldTextInput
                id={`${formId}title`}
                name="title"
                className={css.title}
                type="text"
                label={intl.formatMessage({ id: 'EditListingDetailsForm.title' })}
                placeholder={titlePlaceholder}
                validate={composeValidators(required(titleRequiredMessage), maxLength60Message, minLength2Message)}
              />
            ) : null}
          </div>

          {![CATEGORY_SHOES, CATEGORY_GOWNS, CATEGORY_DRESSES].includes(productType) ? <div className={css.fieldRow}><div>
            <label><FormattedMessage id={[CATEGORY_OCCASIONS].includes(productType) ? "EditListingDetailsForm.subCategory"
              :
              [CATEGORY_ATTIRE].includes(productType) ?
                "EditListingDetailsForm.occasion"
                : [CATEGORY_ACCESSORY].includes(productType) ? "EditListingDetailsForm.subCategoryAcc"
                  : "EditListingDetailsForm.subCategoryOther"} /> </label>
            <FieldMultiSelect
              isMulti={[CATEGORY_OCCASIONS, CATEGORY_ATTIRE].includes(productType) ? true : false}
              isRequired={true}
              className={css.input}
              id={'category'}
              name={'category'}
              fontClass={css.fontClass}
              options={filterCategory.sort((a, b) => a.label.localeCompare(b.label))}
              autoComplete={'category'}
              defaultValue={projectType || ''}
              validate={subCategoryRequired}
            />
          </div> </div> : null}


          {([CATEGORY_OCCASIONS, CATEGORY_SHOES, CATEGORY_ATTIRE].includes(productType) || styleSubAccessories.includes(category?.label)) ? (<div className={css.fieldRow}> <div>
            <label><FormattedMessage id={"EditListingAppearanceForm.style"} /></label>
            <FieldMultiSelect
              isMulti={[CATEGORY_SHOES].includes(productType) ? true : false}
              isRequired={true}
              className={css.input}
              id={'style'}
              name={'style'}
              fontClass={css.fontClass}
              options={(styleSubAccessories.includes(category?.label) && !Array.isArray(category)) ? filterAccessoriesStyle.sort((a, b) => a.label.localeCompare(b.label)) : filterStyles.sort((a, b) => a.label.localeCompare(b.label))}
              autoComplete={'style'}
              defaultValue={style || ''}
              validate={subCategoryRequired}
            />
          </div> </div>) : null}


          <div className={classNames(css.fieldRow, (brand && brand?.label == OTHER_BRAND) && css.otherBrand)}>
            {[CATEGORY_GOWNS].includes(productType) ?
              <>
                <div>
                  <label><FormattedMessage id={"EditListingAppearanceForm.designer"} /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'designer'}
                    name={'designer'}
                    options={filterDesigners.sort((a, b) => a.label.localeCompare(b.label))}
                    fontClass={css.fontClass}
                    autoComplete={'designer'}
                    defaultValue={designer || ''}
                    validate={subCategoryRequired}
                  />
                </div>
                <div>
                  <label><FormattedMessage id={"EditListingDetailsForm.brand"} /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'brand'}
                    name={'brand'}
                    options={listingBrands.sort((a, b) => a.label.localeCompare(b.label))}
                    fontClass={css.fontClass}
                    autoComplete={'brand'}
                    defaultValue={brand || ''}
                    validate={subCategoryRequired}
                  />
                </div>
              </>
              : [CATEGORY_DRESSES].includes(productType) ?
                <div>
                  <label><FormattedMessage id={"EditListingDetailsForm.brand"} /></label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'brand'}
                    name={'brand'}
                    options={listingBrands.sort((a, b) => a.label.localeCompare(b.label))}
                    fontClass={css.fontClass}
                    autoComplete={'brand'}
                    defaultValue={brand || ''}
                    validate={subCategoryRequired}
                  />
                </div> : <FieldTextInput
                  id={`${formId}brand`}
                  name="brand"
                  className={css.title}
                  type="text"
                  label={intl.formatMessage({ id: "EditListingDetailsForm.brand" })}
                  placeholder={intl.formatMessage({ id: 'EditListingDetailsForm.otherBrandPlaceholder' })}
                  validate={brandRequired}
                />

            }

          </div>

          <div>
            <label><FormattedMessage id="EditListingTermsOfSaleForm.originalOwner" /></label>
            <FieldMultiSelect
              isMulti={false}
              isRequired={true}
              className={css.Listingtitle}
              id={'originalOwner'}
              name={'originalOwner'}
              fontClass={css.fontClass}
              options={originalOwners}
              autoComplete={'originalOwner'}
              defaultValue={values?.style || ''}
            // validate={subCategoryRequired}
            />
          </div>

          <div>
            <label><FormattedMessage id="EditListingTermsOfSaleForm.originalReceipt" /></label>
            <FieldMultiSelect
              isMulti={false}
              isRequired={true}
              className={css.Listingtitle}
              id={'originalReceipt'}
              name={'originalReceipt'}
              fontClass={css.fontClass}
              options={originalReceipts}
              autoComplete={'originalReceipt'}
              defaultValue={values?.style || ''}
            // validate={subCategoryRequired}
            />
          </div>

          <FieldTextInput
            id="purchasedYear"
            name="purchasedYear"
            label={intl.formatMessage({ id: 'EditListingAppearanceForm.purchasedYear' })}
            validate={yearValid}
            className={css.Listingtitle}
            defaultValue={purchasedYear}
          />

          {invalid ? (
            <span className={css.error}>
              <FormattedMessage
                id="EditListingDetailsForm.errorText"
                values={{ TabName: <b>{OVERVIEW}</b> }}
              />
            </span>
          ) : null}

          <div className={css.buttonContainer}>
            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>

            <Button
              className={css.draftButton}
              type="button"
              disabled={submitDisabled}
              inProgress={saveAsDraft}
              onClick={() => {
                setSaveAsDraft(true)
                const {
                  listingType,
                  unitType,
                  originalOwner,
                  originalReceipt,
                } = values;
                // New values for listing attributes
                const draftValues = {
                  title: title.trim(),
                  publicData: {
                    markForSale: publicData?.markForSale == false ? publicData?.markForSale : true,
                    listingType,
                    transactionProcessAlias: `${DOTS_PROCESS_NAME}/release-1`,
                    unitType,
                    brand: brand?.label ? brand?.label : brand,
                    category: ([CATEGORY_GOWNS, CATEGORY_BRIDAL_GOWNS].includes(publicData.productType)) ? defaultGownCategory : category,
                    updatedAt: Math.floor(Date.now() / 1000),
                    newListingFlow: true,
                    style: (style && Array.isArray(style) && style.length > 0) ? style.map((e) => e.label)
                      : (styleSubAccessories.includes(category?.label) || (![CATEGORY_ACCESSORY].includes(publicData.productType))) ?
                        style?.label : null,
                    role: role?.label,
                    events: events?.label,
                    designer: designer?.label,
                    originalOwner: originalOwner,
                    originalReceipt: originalReceipt,
                    purchasedYear
                  },
                };

                onSaveDraft(draftValues).then(() => {
                  history.push('/listings')
                })
              }}
            >
              <FormattedMessage id="EditListingsDetailsForm.saveAsDraft" />
            </Button>
          </div>
        </Form>
      );
    }}
  />
);

EditListingDetailsFormComponent.defaultProps = {
  className: null,
  formId: 'EditListingDetailsForm',
  fetchErrors: null,
  hasExistingListingType: false,
  listingFieldsConfig: [],
};

EditListingDetailsFormComponent.propTypes = {
  className: string,
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  selectableListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionProcessAlias: string.isRequired,
      unitType: string.isRequired,
    })
  ).isRequired,
  hasExistingListingType: bool,
  listingFieldsConfig: propTypes.listingFieldsConfig,
};

export default compose(injectIntl)(EditListingDetailsFormComponent);
